import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "src/page/store";
import { SearchUserRequest, searchUser } from "src/network/graphql/userService";
import { SearchWalletRequest, searchWallet } from "src/network/graphql/walletService";
import { getGroupDetail, getGroupList, getGroupUser, SearchGroupListRequest } from "src/network/graphql/groupService";
import {
  CreateReimbursementRequest,
  GetReimbursementListRequest,
  UpdateReimbursementRequest,
  UpdateReimbursementStatusRequest,
  createReimbursement,
  getReimbursement,
  getReimbursementList,
  updateReimbursement,
  getReimbursementHistory,
  retryReimbursementSettlement,
  updateReimbursementStatus
} from "src/network/graphql/paymentService";
import { getAllTransaction } from "src/network/graphql/transactionService";
import { Users } from "src/page/users/usersSlice";
import { Wallet } from "src/page/wallets/walletSlice";
import { Group } from "src/page/group/groupSlice";
import { GroupUser, GroupWallet } from "src/page/groupDetail/groupDetailSlice";
import { Transaction } from "src/page/transactions/transactionSlice";
import { structureActivity } from "src/page/activity/activitySlice";
import { ReimbursementStatus } from "src/types/commonTypes";
import { formatFullName } from "src/utils";
import { EMPTY_STRING } from "src/components/constant/constant";

export type EmbeddedReimbursementWallet = Pick<
  Wallet,
  "id" | "name" | "balance" | "walletType" | "participant" | "group"
>;
export type EmbeddedReimbursementUser = Pick<Users, "id" | "fullName" | "firstName" | "lastName" | "email">;

export type Reimbursement = {
  reimbursement_id: string;
  organisation_id: string;
  source_wallet_id: string;
  destination_wallet_id: string;
  amount: number;
  description: string;
  merchant_name: string;
  requested_by: string;
  for_user_ids: string[];
  status: ReimbursementStatus;
  current_wtr_id: string;
  display_id: string;
  created_at: string;
  updated_at: string;
  updated_by: string;
  transaction_date?: string;
  source_wallet?: EmbeddedReimbursementWallet | null;
  destination_wallet?: EmbeddedReimbursementWallet | null;
  requested_by_user?: EmbeddedReimbursementUser | null;
  updated_by_user?: EmbeddedReimbursementUser | null;
};

export const transformEmbeddedWallet = (raw: any): EmbeddedReimbursementWallet | null => {
  if (!raw) return null;

  return {
    id: raw.wallet_id,
    name: raw.name,
    balance: typeof raw.balance === "number" ? raw.balance : 0,
    walletType: raw.wallet_type,
    participant: raw.user
      ? {
          id: raw.user.user_id,
          fullName: formatFullName(raw.user.attributes?.first_name, raw.user.attributes?.last_name),
          firstName: raw.user.attributes?.first_name,
          lastname: raw.user.attributes?.last_name,
          email: raw.user.email,
          phone: EMPTY_STRING,
          display_id: EMPTY_STRING,
          role: "PARTICIPANT"
        }
      : null,
    group: raw.group
      ? {
          group_id: raw.group.group_id,
          name: raw.group.name,
          group_type: raw.group.group_type
        }
      : null
  };
};

const transformEmbeddedUser = (raw: any): EmbeddedReimbursementUser | null => {
  if (!raw) return null;

  const firstName = raw.attributes?.first_name;
  const lastName = raw.attributes?.last_name;

  return {
    id: raw.user_id,
    firstName,
    lastName,
    fullName: formatFullName(firstName, lastName),
    email: raw.email
  };
};

const decorateReimbursement = (raw: any): Reimbursement => ({
  ...raw,
  source_wallet: transformEmbeddedWallet(raw.source_wallet),
  destination_wallet: transformEmbeddedWallet(raw.destination_wallet),
  requested_by_user: transformEmbeddedUser(raw.requested_by_user),
  updated_by_user: transformEmbeddedUser(raw.updated_by_user)
});

// Wallet Transfer Request — the underlying money movement created when a reimbursement is approved.
export type Wtr = {
  wtr_id: string;
  organisation_id: string;
  operation_type: string;
  source_wallet_id: string;
  destination_wallet_id: string;
  amount: number;
  currency: string;
  execution_mode: string;
  status: string;
  approver_role_required?: string;
  scheduled_at?: string;
  purpose?: string;
  context?: string;
  requested_by: string;
  display_id: string;
  updated_by: string;
  version: number;
  created_at: string;
  updated_at: string;
  source_wallet?: EmbeddedReimbursementWallet | null;
  destination_wallet?: EmbeddedReimbursementWallet | null;
  requested_by_user?: EmbeddedReimbursementUser | null;
  updated_by_user?: EmbeddedReimbursementUser | null;
};

export const transformWtr = (raw: any): Wtr | null => {
  if (!raw) return null;

  return {
    ...raw,
    source_wallet: transformEmbeddedWallet(raw.source_wallet),
    destination_wallet: transformEmbeddedWallet(raw.destination_wallet),
    requested_by_user: transformEmbeddedUser(raw.requested_by_user),
    updated_by_user: transformEmbeddedUser(raw.updated_by_user)
  };
};

export type ReimbursementHistoryItem = {
  audit_id: string;
  created_at: string;
  to_status: string;
  actor_id: string;
  op?: string;
  reason?: string;
  changedFields?: string[];
};

const REASON_KEY_BY_STATUS: Record<string, string> = {
  FAILED: "failure_reason",
  REJECTED: "rejection_reason"
};

const parsePayload = (payload: unknown): Record<string, unknown> | null => {
  let parsed: unknown = payload;

  if (typeof payload === "string") {
    try {
      parsed = JSON.parse(payload);
    } catch {
      return null;
    }
  }

  return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
};

const decorateHistoryItem = (raw: any): ReimbursementHistoryItem => {
  const payload = parsePayload(raw.payload);
  const reasonKey = REASON_KEY_BY_STATUS[raw.to_status];
  const reason = reasonKey ? (payload?.[reasonKey] as string | undefined) : undefined;
  const rawChangedFields = payload?.changed_fields;
  const changedFields = Array.isArray(rawChangedFields) ? (rawChangedFields as string[]) : undefined;

  return {
    audit_id: raw.audit_id,
    created_at: raw.created_at,
    to_status: raw.to_status,
    actor_id: raw.actor_id,
    op: raw.op,
    reason,
    changedFields: changedFields?.length ? changedFields : undefined
  };
};

const initialUsers: Users[] = [];
const initialGroups: Group[] = [];
const initialWallets: Wallet[] = [];
const initialGroupUsers: GroupUser[] = [];
const initialGroupWallets: GroupWallet[] = [];
const initialReimbursements: Reimbursement[] = [];

const reimbursementSlice = createSlice({
  name: "reimbursement",
  initialState: {
    userLoading: false,
    groupsLoading: false,
    walletLoading: false,
    groupDetailLoading: false,
    groupUsersLoading: false,
    createLoading: false,
    updateLoading: false,
    statusUpdateLoading: false,
    listLoading: false,
    detailLoading: false,
    users: { list: initialUsers, total: 0 },
    groups: { list: initialGroups, total: 0 },
    wallets: { list: initialWallets, total: 0 },
    groupWallets: initialGroupWallets,
    groupUsers: { list: initialGroupUsers, total: 0 },
    reimbursements: { list: initialReimbursements, total: 0 },
    reimbursementDetail: null as Reimbursement | null,
    reimbursementHistory: { list: [] as ReimbursementHistoryItem[], loading: false },
    wtrTransactions: { list: [] as Transaction[], loading: false }
  },
  reducers: {
    fetchUsersStart: (state) => {
      state.userLoading = true;
    },
    fetchUsersSuccess: (state, action) => {
      state.userLoading = false;
      state.users.list = action.payload?.list ?? [];
      state.users.total = action.payload?.total ?? 0;
    },
    fetchUsersFail: (state) => {
      state.userLoading = false;
    },
    fetchGroupsStart: (state) => {
      state.groupsLoading = true;
    },
    fetchGroupsSuccess: (state, action) => {
      state.groupsLoading = false;
      state.groups.list = action.payload?.list ?? [];
      state.groups.total = action.payload?.total ?? 0;
    },
    fetchGroupsFail: (state) => {
      state.groupsLoading = false;
    },
    fetchWalletsStart: (state) => {
      state.walletLoading = true;
    },
    fetchWalletsSuccess: (state, action) => {
      state.walletLoading = false;
      state.wallets.list = action.payload?.list ?? [];
      state.wallets.total = action.payload?.total ?? 0;
    },
    fetchWalletsFail: (state) => {
      state.walletLoading = false;
    },
    fetchGroupDetailStart: (state) => {
      state.groupDetailLoading = true;
    },
    fetchGroupDetailSuccess: (state, action) => {
      state.groupDetailLoading = false;
      state.groupWallets = action.payload ?? [];
    },
    fetchGroupDetailFail: (state) => {
      state.groupDetailLoading = false;
      state.groupWallets = [];
    },
    fetchGroupUsersStart: (state) => {
      state.groupUsersLoading = true;
    },
    fetchGroupUsersSuccess: (state, action) => {
      state.groupUsersLoading = false;
      state.groupUsers.list = action.payload?.list ?? [];
      state.groupUsers.total = action.payload?.total ?? 0;
    },
    fetchGroupUsersFail: (state) => {
      state.groupUsersLoading = false;
      state.groupUsers.list = [];
      state.groupUsers.total = 0;
    },
    createReimbursementStart: (state) => {
      state.createLoading = true;
    },
    createReimbursementSuccess: (state) => {
      state.createLoading = false;
    },
    createReimbursementFail: (state) => {
      state.createLoading = false;
    },
    updateStatusStart: (state) => {
      state.statusUpdateLoading = true;
    },
    updateStatusSuccess: (state) => {
      state.statusUpdateLoading = false;
    },
    updateStatusFail: (state) => {
      state.statusUpdateLoading = false;
    },
    fetchListStart: (state) => {
      state.listLoading = true;
    },
    fetchListSuccess: (state, action) => {
      state.listLoading = false;
      state.reimbursements.list = action.payload?.list ?? [];
      state.reimbursements.total = action.payload?.total ?? 0;
    },
    fetchListFail: (state) => {
      state.listLoading = false;
    },
    fetchDetailStart: (state) => {
      state.detailLoading = true;
    },
    fetchDetailSuccess: (state, action) => {
      state.detailLoading = false;
      state.reimbursementDetail = action.payload ?? null;
    },
    fetchDetailFail: (state) => {
      state.detailLoading = false;
      state.reimbursementDetail = null;
    },
    resetWalletsAction: (state) => {
      state.wallets.list = [];
      state.wallets.total = 0;
    },
    resetGroupDetailAction: (state) => {
      state.groupWallets = [];
    },
    updateReimbursementStart: (state) => {
      state.updateLoading = true;
    },
    updateReimbursementSuccess: (state) => {
      state.updateLoading = false;
    },
    updateReimbursementFail: (state) => {
      state.updateLoading = false;
    },
    resetGroupUsersAction: (state) => {
      state.groupUsers.list = [];
      state.groupUsers.total = 0;
    },
    resetDetailAction: (state) => {
      state.reimbursementDetail = null;
    },
    updateItemInList: (state, action) => {
      const idx = state.reimbursements.list.findIndex((r) => r.reimbursement_id === action.payload?.reimbursement_id);

      if (idx !== -1) state.reimbursements.list[idx] = action.payload;
    },
    fetchHistoryStart: (state) => {
      state.reimbursementHistory.loading = true;
    },
    fetchHistorySuccess: (state, action) => {
      state.reimbursementHistory.loading = false;
      state.reimbursementHistory.list = action.payload ?? [];
    },
    fetchHistoryFail: (state) => {
      state.reimbursementHistory.loading = false;
      state.reimbursementHistory.list = [];
    },
    resetHistoryAction: (state) => {
      state.reimbursementHistory.list = [];
      state.reimbursementHistory.loading = false;
    },
    fetchWtrTransactionsStart: (state) => {
      state.wtrTransactions.loading = true;
    },
    fetchWtrTransactionsSuccess: (state, action) => {
      state.wtrTransactions.loading = false;
      state.wtrTransactions.list = action.payload ?? [];
    },
    fetchWtrTransactionsFail: (state) => {
      state.wtrTransactions.loading = false;
      state.wtrTransactions.list = [];
    },
    resetWtrTransactionsAction: (state) => {
      state.wtrTransactions.list = [];
      state.wtrTransactions.loading = false;
    }
  }
});

const {
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFail,
  fetchGroupsStart,
  fetchGroupsSuccess,
  fetchGroupsFail,
  fetchWalletsStart,
  fetchWalletsSuccess,
  fetchWalletsFail,
  fetchGroupDetailStart,
  fetchGroupDetailSuccess,
  fetchGroupDetailFail,
  fetchGroupUsersStart,
  fetchGroupUsersSuccess,
  fetchGroupUsersFail,
  createReimbursementStart,
  createReimbursementSuccess,
  createReimbursementFail,
  updateStatusStart,
  updateStatusSuccess,
  updateStatusFail,
  fetchListStart,
  fetchListSuccess,
  fetchListFail,
  fetchDetailStart,
  fetchDetailSuccess,
  fetchDetailFail,
  updateReimbursementStart,
  updateReimbursementSuccess,
  updateReimbursementFail,
  fetchHistoryStart,
  fetchHistorySuccess,
  fetchHistoryFail,
  fetchWtrTransactionsStart,
  fetchWtrTransactionsSuccess,
  fetchWtrTransactionsFail
} = reimbursementSlice.actions;

export const {
  resetWalletsAction,
  resetGroupDetailAction,
  resetGroupUsersAction,
  resetDetailAction,
  resetHistoryAction,
  resetWtrTransactionsAction,
  updateItemInList
} = reimbursementSlice.actions;

export const fetchReimbursementUsersAction = (data: SearchUserRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUsersStart());
    try {
      const response = await searchUser({
        user_filter: data.user_filter,
        size: data.size,
        from: data.from
      });
      const list = response.data.search_user.user_listing.map((user: any) => ({
        id: user.display_id,
        firstName: user.attributes.first_name,
        lastName: user.attributes.last_name,
        email: user.email,
        currentOrganisationRole: user.current_organisation_role,
        status: user.is_active,
        userId: user.user_id
      }));

      dispatch(fetchUsersSuccess({ list, total: response.data.search_user.total_size }));
    } catch {
      dispatch(fetchUsersFail());
    }
  };
};

export const fetchReimbursementGroupsAction = (data: SearchGroupListRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupsStart());
    try {
      const response = await getGroupList(data);

      dispatch(
        fetchGroupsSuccess({
          list: response.data.search_group.items,
          total: response.data.search_group.total_size
        })
      );
    } catch {
      dispatch(fetchGroupsFail());
    }
  };
};

export const fetchReimbursementWalletsAction = (data: SearchWalletRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWalletsStart());
    try {
      const requestData = {
        ...data,
        walletFilter: { ...data.walletFilter, wallet_status: "ACTIVE" }
      };
      const response = await searchWallet(requestData);
      const list = response.data.search_wallet.items.map((wallet: any) => ({
        id: wallet.wallet_id,
        displayId: wallet.display_id,
        description: wallet.description,
        balance: wallet.balance,
        walletType: wallet.wallet_type,
        name: wallet.name,
        participant: wallet.user
          ? {
              role: "PARTICIPANT",
              fullName: formatFullName(wallet.user?.attributes?.first_name, wallet.user?.attributes?.last_name),
              email: wallet?.user?.email,
              id: wallet?.user?.user_id,
              display_id: wallet?.user?.display_id
            }
          : null,
        group: wallet?.group
      }));

      dispatch(fetchWalletsSuccess({ list, total: response.data.search_wallet.total_size ?? list.length }));
    } catch {
      dispatch(fetchWalletsFail());
    }
  };
};

export const fetchReimbursementGroupWalletsAction = (groupId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupDetailStart());
    try {
      const response = await getGroupDetail(groupId);

      dispatch(fetchGroupDetailSuccess(response.data.get_group?.wallets ?? []));
    } catch {
      dispatch(fetchGroupDetailFail());
    }
  };
};

export const fetchReimbursementGroupUsersAction = (groupId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupUsersStart());
    try {
      const response = await getGroupUser(groupId);

      dispatch(
        fetchGroupUsersSuccess({
          list: response.data.get_group_users,
          total: response.data.get_group_users.length
        })
      );
    } catch {
      dispatch(fetchGroupUsersFail());
    }
  };
};

export const createReimbursementAction = (data: CreateReimbursementRequest, cb?: (_id: string) => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createReimbursementStart());
    try {
      const response = await createReimbursement(data);
      const id = response?.data?.create_reimbursement?.reimbursement_id;

      dispatch(createReimbursementSuccess());
      if (id && cb) cb(id);
      return id;
    } catch {
      dispatch(createReimbursementFail());
      return null;
    }
  };
};

export const updateReimbursementAction = (data: UpdateReimbursementRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateReimbursementStart());
    try {
      await updateReimbursement(data);
      dispatch(updateReimbursementSuccess());
      if (cb) cb();
    } catch {
      dispatch(updateReimbursementFail());
    }
  };
};

export const updateReimbursementStatusAction = (data: UpdateReimbursementStatusRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateStatusStart());
    try {
      await updateReimbursementStatus(data);

      dispatch(updateStatusSuccess());
      if (cb) cb();
    } catch {
      dispatch(updateStatusFail());
    }
  };
};

export const retryReimbursementSettlementAction = (reimbursementId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateStatusStart());
    try {
      await retryReimbursementSettlement(reimbursementId);
      dispatch(updateStatusSuccess());
      if (cb) cb();
    } catch {
      dispatch(updateStatusFail());
    }
  };
};

export const fetchReimbursementListAction = (data: GetReimbursementListRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchListStart());
    try {
      const response = await getReimbursementList(data);

      dispatch(
        fetchListSuccess({
          list: (response.data.search_reimbursement.items ?? []).map(decorateReimbursement),
          total: response.data.search_reimbursement.total_size
        })
      );
    } catch {
      dispatch(fetchListFail());
    }
  };
};

export const getReimbursementDetailAction = (reimbursementId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchDetailStart());
    try {
      const response = await getReimbursement(reimbursementId);

      const detail = response.data.get_reimbursement;

      dispatch(fetchDetailSuccess(detail ? decorateReimbursement(detail) : null));
    } catch {
      dispatch(fetchDetailFail());
    }
  };
};

export const refreshReimbursementInListAction = (reimbursementId: string) => {
  return async (dispatch: StoreDispatch) => {
    try {
      const response = await getReimbursement(reimbursementId);
      const item = response.data.get_reimbursement;

      if (item) dispatch(updateItemInList(decorateReimbursement(item)));
    } catch {
      // silent — list stays as-is
    }
  };
};

export const fetchReimbursementHistoryAction = (reimbursementId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchHistoryStart());
    try {
      const response = await getReimbursementHistory(reimbursementId);
      const list = (response.data.get_reimbursement_history ?? []).map(decorateHistoryItem);

      dispatch(fetchHistorySuccess(list));
    } catch {
      dispatch(fetchHistoryFail());
    }
  };
};

// The actual wallet movements (debit + credit legs) behind a reimbursement's transfer.
export const fetchWtrTransactionsAction = (reimbursementId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWtrTransactionsStart());
    try {
      const response = await getAllTransaction({
        from: 0,
        size: 10,
        filter: { reimbursement_id: reimbursementId }
      });
      const list = (response?.data?.search_activity?.activity_listing ?? []).map(structureActivity);

      dispatch(fetchWtrTransactionsSuccess(list));
    } catch {
      dispatch(fetchWtrTransactionsFail());
    }
  };
};

export default reimbursementSlice.reducer;
